import BigNumber from 'bignumber.js';
import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { Trans } from '@lingui/react';
import { logger } from '@orionprotocol/common';
import { Button, Icon, Input } from '@orionprotocol/ui';
import { applyPrecision, limitByPrecision, useBigNumber } from '@orionprotocol/utils';
import { useSelector } from 'react-redux';

import {
  orionToLumiaConverterContractAddress,
  useOrionToLumiaConverter,
} from '../../entities/swap';
import { useAppChainId, useIsLogged } from '../../shared/api';
import { useCurrentChainBalances } from '../../shared/api/user/useCurrentChainBalances';
import {
  getAssetToAddress,
  getAssetToDecimals,
} from '../../shared/redux/selectors';
import {
  useLumiaPrice,
  useORNPrice,
  useTokenAllowance,
  useReadOnly,
} from '../../shared/hooks';
import { useDialog } from '../../shared/context';
import { GradientText } from '../../shared/ui/GradientText';
import NetworkProtectionTooltipWrapper from '../../shared/ui/NetworkProtectionTooltipWrapper';
import { useUnsupportedNetworkLevel } from '../../entities/governance';

import styles from './Swap.module.scss';

export function Swap(): JSX.Element {
  const isLogged = useIsLogged();
  const { openDialog, closeDialog } = useDialog();
  const lumiaPrice = useLumiaPrice();
  const ornPrice = useORNPrice();
  const chainId = useAppChainId();
  const readOnly = useReadOnly();

  const [swapAmount, setSwapAmount] = useState<string>('');
  const swapAmountBN = useBigNumber(swapAmount);

  const [level] = useUnsupportedNetworkLevel();

  const ornTip = `$${applyPrecision(
    BigNumber(ornPrice ?? 0).multipliedBy(swapAmountBN),
    2,
  )}`;
  const lumiaTip = `$${applyPrecision(
    BigNumber(lumiaPrice ?? 0).multipliedBy(swapAmountBN),
    2,
  )}`;

  const isLockButtonDisabled = isLogged && (swapAmount === '' || swapAmountBN.isZero());

  const balances = useCurrentChainBalances();

  const ornBalance = balances?.wallet.ORN;
  const ornBalanceBN = useBigNumber(ornBalance ?? 0);

  const assetsToAddress = useSelector(getAssetToAddress);
  const assetInputAddress = assetsToAddress?.ORN;
  const assetToDecimals = useSelector(getAssetToDecimals);

  const { convertOrionToLumia } = useOrionToLumiaConverter();

  const { allowance, approveInfinite } = useTokenAllowance(
    assetInputAddress,
    orionToLumiaConverterContractAddress[chainId],
  );

  useEffect(() => {
    if (level === undefined) {
      closeDialog('UnsupportedNetwork');
    } else {
      openDialog('UnsupportedNetwork');
    }
  }, [closeDialog, level, openDialog]);

  const needsAllowance = allowance?.isLessThan(swapAmount) ?? false;

  const hasBalanceError =
    (!ornBalanceBN?.isGreaterThanOrEqualTo(swapAmount) || needsAllowance) ?? false;

  let buttonText = <Trans id="swap" message="Swap" />;

  if (!isLogged) {
    buttonText = (
      <>
        <Trans id="connect_wallet" message="Connect wallet" />
        <Icon icon="chevron" className={styles.chevron} />
      </>
    );
  } else if (readOnly) {
    buttonText = <Trans id="switch_chain" message="Switch chain" />;
  } else if (isLockButtonDisabled) {
    buttonText = <Trans id="enter_lock_amount" message="Enter swap amount" />;
  } else if (needsAllowance) {
    buttonText = <Trans id="allow_to_use_orn" message="Allow to use ORN" />;
  } else if (hasBalanceError) {
    buttonText = <Trans id="enter_valid_amount" message="Enter valid amount" />;
  }

  const handleSwap = useCallback(() => {
    if (!isLogged) {
      openDialog('ConnectWallet');
      return;
    }

    if (needsAllowance) {
      approveInfinite();
    }

    if (hasBalanceError) {
      return;
    }

    convertOrionToLumia(
      swapAmountBN
        .multipliedBy(BigNumber(10).pow(assetToDecimals?.ORN ?? 8))
        .precision(assetToDecimals?.ORN ?? 8, BigNumber.ROUND_DOWN)
        .toNumber(),
    )
      .then(() => {
        setSwapAmount('');
      })
      .catch((e) => {
        logger.error(e);
      });
  }, [
    approveInfinite,
    assetToDecimals?.ORN,
    convertOrionToLumia,
    hasBalanceError,
    isLogged,
    needsAllowance,
    openDialog,
    swapAmountBN,
  ]);

  return (
    <div className={styles.root}>
      <div className={styles.bgImageContainer} />
      <div className={styles.container}>
        <GradientText textClassName={styles.headerTitle}>
          <Trans id="token_swap" message="Token swap" />
        </GradientText>

        <div className={styles.swapContainer}>
          <GradientText className={styles.titleContainer} textClassName={styles.title}>
            <Trans id="swap_orn_to_lumia" message="Swap ORN to LUMIA" />
          </GradientText>
          <div className={styles.content}>
            <div className={styles.block}>
              <h4 className={styles.subtitle}>ORN</h4>
              <Input
                leftContent={
                  <Icon
                    icon="orn"
                    collection="currency"
                    className={styles.token}
                  />
                }
                value={swapAmount}
                placeholder="0"
                onChange={(e: ChangeEvent<HTMLInputElement>): void => {
                  setSwapAmount( limitByPrecision(e.target.value, 18) ); 
                }}
                maxLength={18}
                // tip={ornTip}
                rightContent={
                  <span
                    className={styles.tokensAvailable}
                    onClick={(): void => {
                      setSwapAmount(ornBalanceBN.toString());
                    }}
                  >
                    <Trans id="available" message="Available" />
                    {': '}
                    <span className={styles.amount}>
                      {applyPrecision(ornBalanceBN, 4)}
                    </span>
                  </span>
                }
              />
            </div>

            <div className={styles.divider} />
            <div className={styles.block}>
              <GradientText className={styles.titleContainer} textClassName={styles.title}>
                <Trans id="you_will_receive" message="You will receive" />
              </GradientText>
              <h4 className={styles.subtitle}>LUMIA</h4>
              <Input
                leftContent={
                  <Icon icon="lumiaToken" className={styles.token} />
                }
                value={swapAmount}
                placeholder="0"
                onChange={(e: ChangeEvent<HTMLInputElement>): void =>
                  setSwapAmount(e.target.value)
                }
              // tip={lumiaTip}
              />
            </div>
            <NetworkProtectionTooltipWrapper>
              <Button type='outlined' onClick={handleSwap} disabled={isLockButtonDisabled} className={styles.button}>
                {buttonText}
              </Button>
            </NetworkProtectionTooltipWrapper>
          </div>
        </div>
      </div>
    </div>
  );
}
